import { z } from 'zod';
import { PASSWORD_REGEX, PASSWORD_RULES_MSG } from './signup.schema';

// ============================================================================
// Details step (email, password)
// ============================================================================
export const profileCompletionDetailsSchema = z
  .object({
    email: z
      .string()
      .min(1, 'Email is required')
      .email('Please enter a valid email address'),
    password: z
      .string()
      .min(1, 'Password is required')
      .regex(PASSWORD_REGEX, PASSWORD_RULES_MSG),
    confirm_password: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  });

export type ProfileCompletionDetailsForm = z.infer<
  typeof profileCompletionDetailsSchema
>;

// ============================================================================
// Profile step
// ============================================================================
export const profileCompletionProfileSchema = z.object({
  dob: z.string().min(1, 'Date of birth is required'),
  gender: z.string().min(1, 'Please select your gender'),
  country: z.string().min(1, 'Country is required'),
  state: z.string().min(1, 'State is required'),
  city: z.string().min(1, 'City is required'),
  preferred_language: z.string().optional(),
});

export type ProfileCompletionProfileForm = z.infer<
  typeof profileCompletionProfileSchema
>;

// ============================================================================
// Signup profile step (Universal Signup Step 3) — same API shape as basic profile
// but spoken language is optional here; it stays mandatory in ProfileCompletionGate /
// BasicProfileForm after signup.
// ============================================================================
export const signupProfileSchema = profileCompletionProfileSchema
  .omit({ preferred_language: true })
  .extend({
    preferred_language: z.string().optional(),
    hasAvatar: z.boolean(),
    email: z.string().optional(),
    _role: z.string().optional(),
    _emailVerified: z.boolean().optional(), // when false, user skipped verification — email not required
  })
  .refine((data) => data.hasAvatar, {
    message: 'Please upload a profile photo',
    path: ['avatar'],
  })
  .superRefine((data, ctx) => {
    if (data._role !== 'user') return;
    if (data._emailVerified === false) return; // Skipped verification: email optional
    const email = typeof data.email === 'string' ? data.email.trim() : '';
    if (!email) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Email is required',
        path: ['email'],
      });
      return;
    }
    if (!z.string().email().safeParse(email).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid email address',
        path: ['email'],
      });
    }
  });

export type SignupProfileForm = z.infer<typeof signupProfileSchema>;
